interface EquityPoint {
  time: number;
  equity: number;
}

interface EquityCurveProps {
  points: EquityPoint[];
}

export function EquityCurve({ points }: EquityCurveProps) {
  if (!points || points.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <span className="muted">暂无权益数据</span>
      </div>
    );
  }

  const W = 600;
  const H = 160;
  const PAD = { top: 12, right: 16, bottom: 24, left: 52 };

  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const equities = points.map((p) => p.equity);
  const minEq = Math.min(...equities);
  const maxEq = Math.max(...equities);
  const range = maxEq - minEq || 1;

  const times = points.map((p) => p.time);
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const timeRange = maxT - minT || 1;

  const toX = (t: number) => PAD.left + ((t - minT) / timeRange) * innerW;
  const toY = (e: number) => PAD.top + (1 - (e - minEq) / range) * innerH;

  const polyline = points
    .map((p) => `${toX(p.time).toFixed(1)},${toY(p.equity).toFixed(1)}`)
    .join(' ');

  // Fill polygon (close path down to bottom)
  const firstX = toX(points[0].time).toFixed(1);
  const lastX = toX(points[points.length - 1].time).toFixed(1);
  const bottomY = (PAD.top + innerH).toFixed(1);
  const fillPoints = `${firstX},${bottomY} ${polyline} ${lastX},${bottomY}`;

  // Y-axis labels: min, mid, max
  const midEq = (minEq + maxEq) / 2;
  const fmt = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0);

  // X-axis: first and last date labels
  const fmtDate = (ms: number) =>
    new Date(ms).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit', timeZone: 'UTC' });

  const finalEquity = equities[equities.length - 1];
  const startEquity = equities[0];
  const pnlSign = finalEquity >= startEquity ? '+' : '';
  const netPnl = finalEquity - startEquity;

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">权益曲线</span>
        <span style={{
          fontWeight: 600,
          fontSize: '0.9rem',
          color: netPnl >= 0 ? '#4ade80' : '#f87171'
        }}>
          {pnlSign}{netPnl.toFixed(2)} USD
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        aria-label="权益曲线图"
      >
        {/* Grid lines */}
        {[0, 0.5, 1].map((frac) => {
          const y = PAD.top + frac * innerH;
          return (
            <line
              key={frac}
              x1={PAD.left}
              y1={y}
              x2={PAD.left + innerW}
              y2={y}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="1"
            />
          );
        })}

        {/* Fill area */}
        <polygon
          points={fillPoints}
          fill="rgba(99,179,237,0.08)"
        />

        {/* Main line */}
        <polyline
          points={polyline}
          fill="none"
          stroke="#63b3ed"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Y-axis labels */}
        {[
          { label: fmt(maxEq), y: PAD.top },
          { label: fmt(midEq), y: PAD.top + innerH / 2 },
          { label: fmt(minEq), y: PAD.top + innerH },
        ].map(({ label, y }) => (
          <text
            key={label}
            x={PAD.left - 4}
            y={y + 4}
            textAnchor="end"
            fontSize="10"
            fill="rgba(255,255,255,0.35)"
          >
            {label}
          </text>
        ))}

        {/* X-axis date labels */}
        <text
          x={PAD.left}
          y={H - 4}
          textAnchor="start"
          fontSize="10"
          fill="rgba(255,255,255,0.35)"
        >
          {fmtDate(minT)}
        </text>
        <text
          x={PAD.left + innerW}
          y={H - 4}
          textAnchor="end"
          fontSize="10"
          fill="rgba(255,255,255,0.35)"
        >
          {fmtDate(maxT)}
        </text>
      </svg>
    </div>
  );
}
